/**
 * useDirectUpload — orchestrates the signed-URL upload dance (Phase B2.6).
 *
 *  1. Mint a signed PUT URL via the BE
 *  2. PUT the file bytes directly to the bucket (no BE proxy) using fetch
 *  3. Return the canonical public URL the BE persists on commit
 *
 * Uses XMLHttpRequest under the hood so we can wire `progress` events
 * — fetch() doesn't expose upload progress as of 2026.
 */

import * as React from "react";
import { useMintUploadUrl } from "./api";
import type { AssetKind } from "./types";

type UploadState = "idle" | "minting" | "uploading" | "done" | "error";

export interface DirectUploadResult {
  state: UploadState;
  progress: number;
  error: string | null;
  publicUrl: string | null;
  upload: (file: File) => Promise<string>;
  reset: () => void;
}

export function useDirectUpload(tenantSlug: string, kind: AssetKind): DirectUploadResult {
  const mint = useMintUploadUrl(tenantSlug, kind);
  const [state, setState] = React.useState<UploadState>("idle");
  const [progress, setProgress] = React.useState(0);
  const [publicUrl, setPublicUrl] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const reset = React.useCallback(() => {
    setState("idle");
    setProgress(0);
    setPublicUrl(null);
    setError(null);
    mint.reset();
  }, [mint]);

  const upload = React.useCallback(
    async (file: File): Promise<string> => {
      setError(null);
      setProgress(0);
      setState("minting");

      const signed = await mint.mutateAsync({
        content_type: file.type || "application/octet-stream",
        file_size: file.size,
      });

      setState("uploading");

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", signed.put_url);
        for (const [k, v] of Object.entries(signed.required_headers)) {
          // x-goog-content-length-range is signed-in to GCS — we MUST send
          // the same value the signer baked in.
          xhr.setRequestHeader(k, v);
        }
        xhr.upload.addEventListener("progress", (ev) => {
          if (ev.lengthComputable) {
            setProgress(Math.round((ev.loaded / ev.total) * 100));
          }
        });
        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(
              new Error(
                `Upload failed: ${xhr.status} ${xhr.statusText} ${xhr.responseText.slice(0, 200)}`,
              ),
            );
          }
        });
        xhr.addEventListener("error", () => reject(new Error("Network error during upload.")));
        xhr.addEventListener("abort", () => reject(new Error("Upload aborted.")));
        xhr.send(file);
      });

      setProgress(100);
      setPublicUrl(signed.public_url);
      setState("done");
      return signed.public_url;
    },
    [mint],
  );

  React.useEffect(() => {
    if (mint.isError && mint.error) {
      setError(mint.error.message);
      setState("error");
    }
  }, [mint.isError, mint.error]);

  return { state, progress, error, publicUrl, upload, reset };
}
